import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Boxes, TrendingUp, TrendingDown, Wallet, AlertTriangle, Clock,
  Sparkles, ArrowRight, CalendarClock, Receipt, LineChart as LineChartIcon,
  EyeOff, Activity,
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Legend,
} from 'recharts';
import AdminLayout from '../components/AdminLayout';
import PushHealthWidget from '../components/PushHealthWidget';
import { useAdminAuth, API } from '../context/AdminAuthContext';
import { useAdminPermissions } from '../hooks/useAdminPermissions';
import Rial from '../components/Rial';
import DeskButton from '../components/DeskButton';

/* -------------------------------------------------------------
 * MyGate-style dashboard — Feb 2026.
 * KPI hero row → Occupancy trend + Priority alerts → Collections
 * vs Outstanding + Order pipeline. Single API round-trip
 * (`GET /api/admin/dashboard/mygate`). Mobile-first: 1 → 2 → 4 col.
 * ----------------------------------------------------------- */

const TEAL = '#0FB5A5';        // MyGate teal — primary trend line
const NAVY = '#0F172A';
const AMBER = '#F59E0B';
const ROSE = '#EF4444';
const INK = '#475569';
const MIST = '#E2E8F0';

// ---- Small building blocks -----------------------------------------
const Kpi = ({ label, value, delta, suffix, testid, tone = 'teal' }) => {
  const positive = typeof delta === 'number' ? delta >= 0 : null;
  const toneCls = tone === 'rose' ? 'text-rose-600' : 'text-emerald-600';
  return (
    <div className="rounded-2xl bg-white border border-neutral-200 p-4 sm:p-5 hover:shadow-sm transition" data-testid={testid}>
      <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.14em] text-neutral-500">{label}</div>
      <div className="mt-1.5 text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#0F172A] leading-none">
        {value}{suffix ? <span className="text-neutral-400 text-lg font-semibold ml-1">{suffix}</span> : null}
      </div>
      {delta !== null && delta !== undefined && (
        <div className={`mt-2 text-[11px] font-semibold flex items-center gap-1 ${positive ? toneCls : 'text-rose-600'}`}>
          {positive ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
          {positive ? '+' : ''}{delta}% vs last month
        </div>
      )}
    </div>
  );
};

const AlertRow = ({ alert }) => {
  const map = {
    expiring: { bar: 'bg-amber-400', ring: 'ring-amber-200', icon: CalendarClock, iconTone: 'text-amber-600', bg: 'bg-amber-50' },
    stalled:  { bar: 'bg-sky-500',   ring: 'ring-sky-200',   icon: Clock,         iconTone: 'text-sky-600',   bg: 'bg-sky-50' },
    overdue:  { bar: 'bg-rose-500',  ring: 'ring-rose-200',  icon: Receipt,       iconTone: 'text-rose-600',  bg: 'bg-rose-50' },
  }[alert.kind] || { bar: 'bg-neutral-400', ring: 'ring-neutral-200', icon: AlertTriangle, iconTone: 'text-neutral-500', bg: 'bg-neutral-50' };
  const Icon = map.icon;
  return (
    <div className={`relative rounded-xl ${map.bg} border border-transparent p-3.5 flex items-start gap-3 group hover:ring-2 ${map.ring} transition`} data-testid={`alert-${alert.kind}`}>
      <div className={`absolute inset-y-2 left-0 w-1 rounded-r ${map.bar}`} />
      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${map.iconTone}`} />
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-bold text-[#0F172A] leading-tight">{alert.title}</div>
        <div className="text-[11px] text-neutral-600 mt-0.5">{alert.hint}</div>
      </div>
      <ArrowRight className="w-4 h-4 text-neutral-300 group-hover:text-neutral-500 shrink-0" />
    </div>
  );
};

const PipelineBar = ({ label, value, max }) => {
  const pct = max ? Math.max(4, Math.round(100 * value / max)) : 0;
  return (
    <div className="flex items-center gap-3">
      <div className="w-24 sm:w-28 text-[12px] font-semibold text-neutral-700 truncate">{label}</div>
      <div className="flex-1 h-2.5 bg-neutral-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-[width] duration-500"
          style={{ width: `${pct}%`, background: value > 0 ? TEAL : '#E5E7EB' }}
        />
      </div>
      <div className="w-10 text-right text-[12px] font-bold tabular-nums text-[#0F172A]">{value}</div>
    </div>
  );
};

// ---- Feb 2026 — Orders & ASN status charts -------------------------
// Client-side rollups of the already-fetched `/admin/orders` and
// `/admin/asn` lists. Statuses marked in the ALERT_* sets render as a
// red high-alert band with a pulsing dot; every other status uses the
// normal theme colour. Clicking a bar deep-links to the filtered list.
const ORDER_ALERT_STATUSES = new Set(['undelivered']);
// Feb 2026 — green "all-good" statuses render in emerald instead of
// the normal theme colour. Lighter tone for Delivered, full tone for
// Received, since "received" means confirmed by the receiving branch
// (one step beyond Delivered).
const ORDER_GREEN_STATUSES = new Set(['delivered', 'received']);
const ORDER_GREEN_SHADES = {
  delivered: '#86EFAC',  // light green
  received: '#16A34A',   // full green
};
// Synthetic buckets we append below the real statuses. The alert flag
// is a visual-only "needs attention" tag; green statuses override it.
const ORDER_SYNTHETIC_KEYS = new Set(['fully_picked', 'partial_picked']);
const ASN_ALERT_STATUSES = new Set(['hold', 'partial_put_away']);
const ASN_GREEN_STATUSES = new Set(['fully_put_away']);

// Human-readable mapping. Any status we see that is NOT here falls
// back to startCase(status).
const ORDER_STATUS_LABELS = {
  draft: 'Draft',
  new: 'New',
  confirmed: 'Confirmed',
  confirmed_by_recipient: 'Confirmed by recipient',
  assigned: 'Assigned',
  picked_up: 'Picked up',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  received: 'Received',
  undelivered: 'Undelivered',
  failed: 'Failed',
  cancelled: 'Cancelled',
  fully_picked: 'Fully Picked',
  partial_picked: 'Partial Picked',
};

const ASN_STATUS_LABELS = {
  draft: 'Draft',
  expected: 'Expected',
  received: 'Received',
  put_away: 'Put away',
  cancelled: 'Cancelled',
  hold: 'Hold',
  fully_put_away: 'Fully Put away',
  partial_put_away: 'Partial Put away',
};

const prettify = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Horizontal status bar — one row per status. `kind` is 'alert' (red
// pulsing dot), 'green' (emerald bar + check dot), or '' (default).
const StatusBar = ({ label, value, max, kind, barColor, color, href, testid }) => {
  const pct = max ? Math.max(4, Math.round(100 * value / max)) : 0;
  const alert = kind === 'alert';
  const green = kind === 'green';
  const resolvedBarColor = alert
    ? '#DC2626'
    : (green ? (barColor || '#16A34A') : color);
  const row = (
    <div
      className={`flex items-center gap-3 rounded-lg px-2 py-1.5 transition ${
        alert
          ? 'bg-red-50/60 ring-1 ring-red-200'
          : green
            ? 'bg-emerald-50/60 ring-1 ring-emerald-200'
            : 'hover:bg-neutral-50'
      }`}
      data-testid={testid}
    >
      <div className="w-28 sm:w-36 flex items-center gap-1.5 shrink-0">
        {alert && (
          <span className="relative inline-flex h-2 w-2 shrink-0" aria-hidden="true">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-60" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600" />
          </span>
        )}
        {green && (
          <span className="relative inline-flex h-2 w-2 shrink-0 rounded-full" style={{ background: resolvedBarColor }} aria-hidden="true" />
        )}
        <span
          className={`text-[12px] font-semibold truncate ${
            alert ? 'text-red-800' : green ? 'text-emerald-800' : 'text-neutral-700'
          }`}
        >
          {label}
        </span>
      </div>
      <div className="flex-1 h-2.5 bg-neutral-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-[width] duration-500"
          style={{ width: `${pct}%`, background: value > 0 ? resolvedBarColor : '#E5E7EB' }}
        />
      </div>
      <div
        className={`w-10 text-right text-[12px] font-bold tabular-nums ${
          alert ? 'text-red-700' : green ? 'text-emerald-700' : 'text-[#0F172A]'
        }`}
      >
        {value}
      </div>
    </div>
  );
  if (href && value > 0) {
    return <a href={href} className="block">{row}</a>;
  }
  return row;
};

const StatusChartCard = ({
  icon: Icon, iconTone, title, rows, total, alertCount, hrefFor, color, testid,
}) => {
  const max = rows.reduce((m, r) => Math.max(m, r.value || 0), 0);
  return (
    <div
      className="rounded-2xl bg-white border border-neutral-200 p-4 sm:p-5"
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-2 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <Icon className={`w-4 h-4 ${iconTone}`} />
            <h2 className="text-base font-bold text-[#0F172A]">{title}</h2>
          </div>
          <div className="mt-1 text-[11px] text-neutral-500">
            <span className="font-bold text-[#0F172A]">{total}</span> total
            {alertCount > 0 && (
              <>
                {' · '}
                <span className="inline-flex items-center gap-1 font-bold text-red-700">
                  <AlertTriangle className="w-3 h-3" />
                  {alertCount} on alert
                </span>
              </>
            )}
          </div>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="text-[13px] text-neutral-500 italic py-6 text-center">
          Nothing to show yet.
        </div>
      ) : (
        <div className="space-y-1.5">
          {rows.map((r) => (
            <StatusBar
              key={r.key}
              label={r.label}
              value={r.value}
              max={max}
              kind={r.kind || ''}
              barColor={r.barColor}
              color={color}
              href={hrefFor ? hrefFor(r.key) : undefined}
              testid={`${testid}-row-${r.key}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const StatusCharts = ({ authHeaders }) => {
  const [orders, setOrders] = useState([]);
  const [asns, setAsns] = useState([]);
  const [progress, setProgress] = useState({
    orders: { fully_picked: 0, partial_picked: 0 },
    asns: { fully_put_away: 0, partial_put_away: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: od }, { data: ad }, { data: pg }] = await Promise.all([
          axios.get(`${API}/admin/orders`, { headers: authHeaders }).catch(() => ({ data: [] })),
          axios.get(`${API}/admin/asn`, { headers: authHeaders }).catch(() => ({ data: [] })),
          axios.get(`${API}/admin/dashboard/progress-counts`, { headers: authHeaders })
            .catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        setOrders(Array.isArray(od) ? od : []);
        setAsns(Array.isArray(ad) ? ad : []);
        if (pg) setProgress(pg);
      } catch (e) {
        if (!cancelled) setErr(e?.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authHeaders]);

  const orderRows = useMemo(() => {
    const counts = new Map();
    for (const o of orders) {
      const k = String(o?.status || 'unknown').toLowerCase();
      counts.set(k, (counts.get(k) || 0) + 1);
    }
    const ordered = [
      'draft', 'new', 'confirmed', 'confirmed_by_recipient', 'assigned',
      'picked_up', 'out_for_delivery', 'delivered', 'received',
      'undelivered', 'failed', 'cancelled',
    ];
    const seen = new Set();
    const rows = [];
    const classify = (k) => {
      if (ORDER_ALERT_STATUSES.has(k)) return 'alert';
      if (ORDER_GREEN_STATUSES.has(k)) return 'green';
      return '';
    };
    for (const k of ordered) {
      if (counts.has(k)) {
        rows.push({
          key: k,
          label: ORDER_STATUS_LABELS[k] || prettify(k),
          value: counts.get(k),
          kind: classify(k),
          barColor: ORDER_GREEN_SHADES[k],
        });
        seen.add(k);
      }
    }
    for (const [k, v] of counts.entries()) {
      if (seen.has(k)) continue;
      rows.push({
        key: k,
        label: ORDER_STATUS_LABELS[k] || prettify(k),
        value: v,
        kind: classify(k),
        barColor: ORDER_GREEN_SHADES[k],
      });
    }
    // Append synthetic picking buckets at the bottom.
    rows.push({
      key: 'fully_picked',
      label: ORDER_STATUS_LABELS.fully_picked,
      value: progress?.orders?.fully_picked || 0,
      kind: 'green',
      barColor: '#16A34A',
    });
    rows.push({
      key: 'partial_picked',
      label: ORDER_STATUS_LABELS.partial_picked,
      value: progress?.orders?.partial_picked || 0,
      kind: 'alert',
    });
    return rows;
  }, [orders, progress]);

  const asnRows = useMemo(() => {
    const counts = new Map();
    let heldCount = 0;
    for (const a of asns) {
      if (a?.hold_pallet) heldCount += 1;
      const k = String(a?.status || 'unknown').toLowerCase();
      counts.set(k, (counts.get(k) || 0) + 1);
    }
    const ordered = ['draft', 'expected', 'received', 'put_away', 'cancelled'];
    const seen = new Set();
    const rows = [];
    for (const k of ordered) {
      if (counts.has(k)) {
        rows.push({
          key: k,
          label: ASN_STATUS_LABELS[k] || prettify(k),
          value: counts.get(k),
          kind: '',
        });
        seen.add(k);
      }
    }
    for (const [k, v] of counts.entries()) {
      if (seen.has(k)) continue;
      rows.push({
        key: k,
        label: ASN_STATUS_LABELS[k] || prettify(k),
        value: v,
        kind: '',
      });
    }
    // Synthetic Hold row — ASN hold is a flag, not a native status.
    rows.push({
      key: 'hold',
      label: 'Hold',
      value: heldCount,
      kind: 'alert',
    });
    // Synthetic put-away buckets at the bottom.
    rows.push({
      key: 'fully_put_away',
      label: ASN_STATUS_LABELS.fully_put_away,
      value: progress?.asns?.fully_put_away || 0,
      kind: 'green',
    });
    rows.push({
      key: 'partial_put_away',
      label: ASN_STATUS_LABELS.partial_put_away,
      value: progress?.asns?.partial_put_away || 0,
      kind: 'alert',
    });
    return rows;
  }, [asns, progress]);

  const orderTotal = orders.length;
  const orderAlerts = orderRows
    .filter((r) => r.kind === 'alert' && !ORDER_SYNTHETIC_KEYS.has(r.key))
    .reduce((s, r) => s + r.value, 0)
    + (progress?.orders?.partial_picked || 0);
  const asnTotal = asns.length;
  const asnAlerts = (asnRows.find((r) => r.key === 'hold')?.value || 0)
    + (progress?.asns?.partial_put_away || 0);

  if (loading) {
    return (
      <div
        className="rounded-2xl bg-white border border-neutral-200 p-6 text-center text-sm text-neutral-500"
        data-testid="dashboard-status-charts-loading"
      >
        Loading orders & ASN status…
      </div>
    );
  }
  if (err) {
    return (
      <div
        className="rounded-2xl bg-rose-50 border border-rose-200 p-6 text-sm text-rose-700"
        data-testid="dashboard-status-charts-error"
      >
        {err}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="dashboard-status-charts">
      <StatusChartCard
        icon={Receipt}
        iconTone="text-[#0078D4]"
        title="Orders by status"
        rows={orderRows}
        total={orderTotal}
        alertCount={orderAlerts}
        hrefFor={(k) => `/admin/orders?status=${encodeURIComponent(k)}`}
        color={TEAL}
        testid="dashboard-orders-by-status"
      />
      <StatusChartCard
        icon={Boxes}
        iconTone="text-[#0FB5A5]"
        title="ASNs by status"
        rows={asnRows}
        total={asnTotal}
        alertCount={asnAlerts}
        hrefFor={(k) => k === 'hold' ? `/admin/goods-receiving?hold=1` : `/admin/goods-receiving?status=${encodeURIComponent(k)}`}
        color={AMBER}
        testid="dashboard-asns-by-status"
      />
    </div>
  );
};

// ---- Main page -----------------------------------------------------
const AdminDashboard = () => {
  const { authHeaders } = useAdminAuth();
  const { can, isSuper } = useAdminPermissions();

  // Field-level widget gating (unchanged contract from the legacy view).
  const showKpis = can('dashboard.field_total_orders') || can('dashboard.field_revenue') || can('dashboard.field_total_products') || isSuper;
  const showTrend = can('dashboard.field_total_products') || isSuper;
  const showCollections = can('dashboard.field_revenue') || isSuper;
  const showPipeline = can('dashboard.field_total_orders') || isSuper;
  const showPushHealth = can('dashboard.field_push_health');
  const hasAnyWidget = showKpis || showTrend || showCollections || showPipeline || showPushHealth;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/admin/dashboard/mygate`, { headers: authHeaders });
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e?.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authHeaders]);

  const pipelineMax = useMemo(() => {
    const rows = data?.pipeline || [];
    return rows.reduce((m, r) => Math.max(m, r.count || 0), 0);
  }, [data]);

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 lg:p-10">
        {/* Header */}
        <div className="mb-6 sm:mb-8 flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-400">Operational</div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0F172A] font-display mt-1">Overview</h1>
            <p className="text-neutral-500 text-sm mt-1 hidden sm:block">
              Live snapshot of stock health, collections and open work — refreshed on load.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">
              <Activity className="w-3 h-3" /> Live
            </span>
            <DeskButton />
          </div>
        </div>

        {loading && (
          <div className="rounded-2xl border border-neutral-200 bg-white p-10 text-center text-neutral-500" data-testid="dashboard-loading">
            Loading dashboard…
          </div>
        )}
        {!loading && err && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700" data-testid="dashboard-error">
            {err}
          </div>
        )}
        {!loading && !err && !hasAnyWidget && !isSuper && (
          <div className="rounded-2xl border border-dashed border-neutral-300 bg-white p-12 text-center" data-testid="dashboard-empty">
            <EyeOff className="w-10 h-10 mx-auto text-neutral-300 mb-3" />
            <div className="text-sm font-semibold text-[#0F172A] mb-1">No dashboard widgets visible</div>
            <div className="text-xs text-neutral-500 max-w-md mx-auto">
              Ask the super admin to grant the relevant <code>dashboard.field_*</code> permissions.
            </div>
          </div>
        )}

        {!loading && !err && data && (
          <>
            {/* KPI HERO ROW */}
            {/* Feb 2026 — KPI tiles row and Occupancy Trend removed
                per user request to declutter the dashboard. Priority
                Alerts stays as a full-width card at the top. */}

            {(data.alerts || []).length >= 0 && (
              <div data-testid="dashboard-alerts-row">
                <div className="rounded-2xl bg-white border border-neutral-200 p-4 sm:p-5" data-testid="dashboard-alerts">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-amber-500" />
                      <h2 className="text-base font-bold text-[#0F172A]">Priority Alerts</h2>
                    </div>
                    {(data.alerts || []).length > 0 && (
                      <span className="text-[10px] font-bold text-rose-700 bg-rose-100 rounded-full px-2 py-0.5">
                        {data.alerts.length} active
                      </span>
                    )}
                  </div>
                  <div className="space-y-2">
                    {(data.alerts || []).length === 0 ? (
                      <div className="text-[13px] text-neutral-500 italic py-6 text-center">
                        Nothing urgent right now.
                      </div>
                    ) : (
                      data.alerts.map((a, i) => <AlertRow key={i} alert={a} />)
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ORDER PIPELINE (full width — Collections moved to bottom) */}
            {showPipeline && (
              <div className="mt-5 sm:mt-6" data-testid="dashboard-pipeline-row">
                <div className="rounded-2xl bg-white border border-neutral-200 p-4 sm:p-5" data-testid="dashboard-pipeline">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Boxes className="w-4 h-4 text-[#0FB5A5]" />
                      <h2 className="text-base font-bold text-[#0F172A]">Order Pipeline</h2>
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 bg-neutral-100 rounded-full px-2 py-0.5">Live</span>
                  </div>
                  <div className="grid gap-2.5 mt-4 sm:grid-cols-2 lg:grid-cols-3">
                    {(data.pipeline || []).map((r, i) => (
                      <PipelineBar key={i} label={r.label} value={r.count} max={pipelineMax} />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {showPipeline && (
              <div className="mt-5 sm:mt-6" data-testid="dashboard-status-section">
                <StatusCharts authHeaders={authHeaders} />
              </div>
            )}

            {showPushHealth && (
              <div className="mt-5 sm:mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
                <PushHealthWidget />
              </div>
            )}

            {/* COLLECTIONS vs OUTSTANDING — pinned to the bottom of
                the dashboard (Feb 2026). */}
            {showCollections && (
              <div className="mt-5 sm:mt-6" data-testid="dashboard-collections-row">
                <div className="rounded-2xl bg-white border border-neutral-200 p-4 sm:p-5" data-testid="dashboard-collections">
                  <div className="flex items-baseline justify-between gap-2 mb-4">
                    <div className="flex items-center gap-2">
                      <Wallet className="w-4 h-4 text-[#0078D4]" />
                      <h2 className="text-base font-bold text-[#0F172A]">Collections vs Outstanding</h2>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 bg-neutral-100 rounded-full px-2 py-0.5 hidden sm:inline">
                        Monthly · <Rial /> OMR
                      </span>
                    </div>
                  </div>
                  <div className="h-52 sm:h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.collections_trend} margin={{ top: 5, right: 10, left: -12, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={MIST} vertical={false} />
                        <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: INK, fontSize: 11 }} />
                        <YAxis tickLine={false} axisLine={false} tick={{ fill: INK, fontSize: 11 }} width={44} />
                        <Tooltip
                          contentStyle={{ borderRadius: 12, border: `1px solid ${MIST}`, fontSize: 12 }}
                          formatter={(v, name) => [Number(v).toFixed(3), name === 'collected' ? 'Collected' : 'Outstanding']}
                        />
                        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} iconType="circle" />
                        <Bar dataKey="collected" name="Collected" fill={TEAL} radius={[6, 6, 0, 0]} maxBarSize={22} />
                        <Bar dataKey="outstanding" name="Outstanding" fill={AMBER} radius={[6, 6, 0, 0]} maxBarSize={22} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
